import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BackHandler, KeyboardAvoidingView, Platform, ScrollView, TouchableOpacity, View, useWindowDimensions } from "react-native";
import { Theme, ThemeConsumer } from "@rneui/themed";
import { useFormik } from "formik";
import Popover from "react-native-popover-view";

//common components
import CommonHeader from "../../shared/commonComponents/commonHeader";
import CommonDialogBox from "../../shared/commonComponents/commonDialogBox";
import ZenSkeleton from "../../shared/commonComponents/zenSkeleton";
import { useAppNavigation } from "../../types/@react-navigation/navigation";

//shared components
import { ZenButton, ZenIcon, ZenSwitch, ZenText, ZenToast } from "zen-core-elements";
import { CustomModal } from "../../shared/commonComponents/customModal";
import CommonTextEditor from "../../shared/commonComponents/commonTextEditor";
import { getScreenPermission, useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getAllReceiptSettings, updateReceiptSettings } from "../../redux/slices/receiptSettingSlice";
import { pageDirty } from "../../redux/slices/commonSlice";
import { config } from "../../shared/environment";
import { getAllReceiptSettingsPayload, ReceiptSettingsFormik, ReceiptSettingsPayload } from "./receiptSettings.module";
import ReceiptPreview from "./receiptPreview";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { shallowEqual } from "react-redux";

type ReceiptSettingsBooleanKeys = {
    [K in keyof ReceiptSettingsFormik]: ReceiptSettingsFormik[K] extends boolean ? K : never
}[keyof ReceiptSettingsFormik];

type ReceiptType = ReceiptSettingsFormik["receiptType"]

const receiptTypes: { label: string, value: ReceiptType, icon: string }[] = [
    { label: "Sales receipt", value: "SALES", icon: "receipt" },
    { label: "Gift receipt", value: "GIFT", icon: "gift-outline" }
]

const toggleData: { label: string, helpText: string, icon: string, formikName: ReceiptSettingsBooleanKeys }[] = [
    { label: "Store information", helpText: "Store name, address and tax number", icon: "storefront-outline", formikName: "showStoreInformation" },
    { label: "Customer information", helpText: "Customer name and mobile number", icon: "account-outline", formikName: "showCustomerInformation" },
    { label: "Staff name", helpText: "Staff member who completed the sale", icon: "badge-account-horizontal-outline", formikName: "showStaffName" },
    { label: "Notes", helpText: "Notes added to the order or gift card", icon: "note-text-outline", formikName: "showNotes" },
    { label: "Total savings", helpText: "Total discount the customer saved", icon: "sale", formikName: "showTotalSavings" },
]

const ReceiptsSettings = () => {
    const { isWrite: isSettingWrite } = useAppSelector(getScreenPermission("SettingStack"), shallowEqual)
    const { width, height } = useWindowDimensions()
    const isLandscape = width > height
    const navigation = useAppNavigation()
    const dispatch = useAppDispatch()
    const insets = useSafeAreaInsets();
    const receiptSettings = useAppSelector((state) => state.receiptSettings?.getAllReceiptSettings)

    const [isLoading, setIsLoading] = useState(!receiptSettings?.value)
    const [saving, setSaving] = useState(false)
    const [preview, setPreview] = useState(false)
    const [leaveVisible, setLeaveVisible] = useState(false)
    //Receipt type waiting for confirmation because the current type has unsaved changes
    const [pendingType, setPendingType] = useState<ReceiptType | null>(null)

    const initialValues = useMemo<ReceiptSettingsFormik>(() => ({
        receiptType: receiptSettings?.value?.[0]?.receiptType ?? "SALES",
        header: receiptSettings?.value?.[0]?.header ?? "",
        footer: receiptSettings?.value?.[0]?.footer ?? "",
        showCustomerInformation: receiptSettings?.value?.[0]?.showCustomerInformation!,
        showStoreInformation: receiptSettings?.value?.[0]?.showStoreInformation!,
        showStaffName: receiptSettings?.value?.[0]?.showStaffName!,
        showNotes: receiptSettings?.value?.[0]?.showNotes!,
        showTotalSavings: receiptSettings?.value?.[0]?.showTotalSavings!
    }), [receiptSettings?.value])

    const formik = useFormik<ReceiptSettingsFormik>({
        initialValues,
        enableReinitialize: true,
        onSubmit: () => { updateReceiptSettingsApi() }
    })
    const isDirty = formik.dirty
    const canSave = isSettingWrite && isDirty && !saving && !isLoading
    const receiptLabel = formik?.values?.receiptType === "SALES" ? "sales" : "gift"

    const updateReceiptSettingsApi = () => {
        setSaving(true)
        const payload: ReceiptSettingsPayload = {
            data: {
                receiptType: formik?.values?.receiptType,
                header: formik?.values?.header,
                footer: formik?.values?.footer,
                showCustomerInformation: formik?.values?.showCustomerInformation,
                showStoreInformation: formik?.values?.showStoreInformation,
                showStaffName: formik?.values?.showStaffName,
                showNotes: formik?.values?.showNotes,
                showTotalSavings: formik?.values?.showTotalSavings,
            },
            storeId: config.storeId
        }
        dispatch(updateReceiptSettings(payload)).unwrap().then((response) => {
            ZenToast({ message: response?.message, type: "success" })
            // Refresh the stored settings so the form treats the saved values as its new baseline
            return dispatch(getAllReceiptSettings({ storeId: config.storeId, receiptType: payload.data.receiptType })).unwrap()
        }).catch((error) => {
            ZenToast({ message: error?.message, type: "error" })
        }).finally(() => {
            setSaving(false)
        })
    }

    useEffect(() => {
        if (!receiptSettings?.value) {
            getReceiptSettings()
        }
    }, [])

    const getReceiptSettings = (type?: ReceiptType) => {
        const payload: getAllReceiptSettingsPayload = {
            storeId: config.storeId,
            receiptType: type ? type : formik?.values?.receiptType
        }
        dispatch(getAllReceiptSettings(payload)).unwrap().catch((error) => {
            ZenToast({ message: error?.message, type: "error" })
            // Go back to the previously loaded receipt type
            formik.resetForm()
        }).finally(() => {
            setIsLoading(false)
        })
    }

    const applyReceiptType = (type: ReceiptType) => {
        setIsLoading(true)
        formik?.setFieldValue("receiptType", type)
        getReceiptSettings(type)
    }

    const handleReceiptTypeChange = (type: ReceiptType) => {
        if (type === formik?.values?.receiptType || isLoading) return
        if (isDirty) {
            setPendingType(type)
        } else {
            applyReceiptType(type)
        }
    }

    useEffect(() => {
        dispatch(pageDirty(isDirty))
        return () => {
            dispatch(pageDirty(false))
        }
    }, [isDirty, dispatch])

    const onLeave = useCallback(() => {
        if (isDirty) {
            setLeaveVisible(true)
            return true
        }
        navigation.goBack()
        return true
    }, [isDirty, navigation])

    useEffect(() => {
        const subscription = BackHandler.addEventListener("hardwareBackPress", onLeave)
        return () => subscription.remove()
    }, [onLeave])

    return (
        <ThemeConsumer>
            {({ theme }: { theme: Theme }) => {
                const style = theme?.receiptSettingsScreenStyle

                const renderSectionHeader = (title: string, subTitle: string) => (
                    <>
                        <ZenText content={title} size={"large"} color={theme?.primary} bold style={style?.cardTitle} />
                        <ZenText content={subTitle} size={theme?.smallHeaderFontSize} color={theme?.grey} bold style={style?.cardSubTitle} />
                    </>
                )

                const receiptTypeCard = (
                    <View style={style?.card}>
                        {renderSectionHeader("Receipt Type", "Choose the receipt you want to customise")}
                        <View style={style?.segmentContainer} accessibilityRole="tablist">
                            {receiptTypes.map((type) => {
                                const isActive = formik?.values?.receiptType === type.value
                                return (
                                    <TouchableOpacity
                                        key={type.value}
                                        style={[style?.segment, isActive && style?.segmentActive]}
                                        onPress={() => handleReceiptTypeChange(type.value)}
                                        disabled={isLoading || saving}
                                        accessibilityRole="tab"
                                        accessibilityState={{ selected: isActive }}
                                        accessibilityLabel={type.label}
                                    >
                                        <ZenIcon type="material-community" name={type.icon} size={18} color={isActive ? theme?.primary : theme?.grey} />
                                        <ZenText content={type.label} size={theme?.smallFontSize} color={isActive ? theme?.primary : theme?.grey} bold={isActive} />
                                    </TouchableOpacity>
                                )
                            })}
                        </View>
                    </View>
                )

                const contentCard = (
                    <View style={style?.card}>
                        {renderSectionHeader("Receipt Content", `Text printed at the top and bottom of every ${receiptLabel} receipt`)}
                        <View style={style?.infoNote}>
                            <ZenIcon name="information-outline" type="material-community" size={18} color={theme?.primary} />
                            <ZenText
                                content="Use the toolbar to make text bold, italic or underlined and to change its alignment."
                                size={theme?.smallHeaderFontSize}
                                color={theme?.lightDark}
                                style={{ flex: 1 }}
                            />
                        </View>
                        {/* Keyed by receipt type so the editor reloads its content when the type changes */}
                        <CommonTextEditor
                            key={`header-${formik?.values?.receiptType}`}
                            label={"Header"}
                            placeholder={"e.g. Welcome to our store!"}
                            disabled={!isSettingWrite}
                            value={formik?.values?.header}
                            onChange={(html) => formik?.setFieldValue("header", html)}
                            containerStyle={{ marginBottom: 15 }}
                        />
                        <CommonTextEditor
                            key={`footer-${formik?.values?.receiptType}`}
                            label={"Footer"}
                            placeholder={"e.g. Thank you for shopping with us!"}
                            disabled={!isSettingWrite}
                            value={formik?.values?.footer}
                            onChange={(html) => formik?.setFieldValue("footer", html)}
                        />
                    </View>
                )

                const displayOptionsCard = (
                    <View style={style?.card}>
                        {renderSectionHeader("Display Options", "Choose the details shown on the printed receipt")}
                        <View style={style?.rowGroup}>
                            {toggleData?.map((data) => {
                                const value = Boolean(formik?.values?.[data?.formikName])
                                return (
                                    <View
                                        key={data.formikName}
                                        style={style?.row}
                                        accessible
                                        accessibilityRole="switch"
                                        accessibilityLabel={data.label}
                                        accessibilityState={{ checked: value, disabled: !isSettingWrite }}
                                    >
                                        <View style={style?.rowIcon}>
                                            <ZenIcon type="material-community" name={data.icon} size={18} color={theme?.primary} />
                                        </View>
                                        <View style={style?.rowLabelWrapper}>
                                            <ZenText content={data?.label} size={"medium"} color={theme?.lightDark} />
                                            <ZenText content={data?.helpText} size={theme?.smallHeaderFontSize} color={theme?.grey} style={style?.helperText} />
                                        </View>
                                        <ZenSwitch
                                            value={value}
                                            activeColor={theme?.primary}
                                            disabled={!isSettingWrite}
                                            activeThumbColor={theme?.light}
                                            inActiveColor={theme?.borderColor}
                                            inactiveThumbColor={theme?.light}
                                            onValueChange={() => { formik.setFieldValue(data?.formikName, !value) }}
                                        />
                                    </View>
                                )
                            })}
                        </View>
                    </View>
                )

                const livePreviewCard = (
                    <View style={style?.card}>
                        {renderSectionHeader("Live Preview", "Updates as you make changes")}
                        <View style={style?.previewPaper}>
                            <ReceiptPreview formikValues={formik?.values} receiptType={formik?.values?.receiptType} theme={theme} />
                        </View>
                    </View>
                )

                const skeleton = (
                    <View style={style?.column}>
                        <View style={[style?.card, style?.skeletonCard]}>
                            <ZenSkeleton width={"45%"} height={18} />
                            <ZenSkeleton width={"70%"} height={12} />
                            <ZenSkeleton height={130} borderRadius={8} />
                            <ZenSkeleton height={130} borderRadius={8} />
                        </View>
                        <View style={[style?.card, style?.skeletonCard]}>
                            <ZenSkeleton width={"45%"} height={18} />
                            <ZenSkeleton height={48} borderRadius={8} />
                            <ZenSkeleton height={48} borderRadius={8} />
                            <ZenSkeleton height={48} borderRadius={8} />
                        </View>
                    </View>
                )

                return (
                    <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined} enabled style={style?.outerContainer}>
                        <CommonHeader headerName="Receipt Settings" backIcon backIconOnpress={onLeave} rightComponent={
                            <View style={style?.headerActions}>
                                {/* In landscape the live preview is always visible */}
                                {!isLandscape && (
                                    <ZenButton
                                        title="Preview"
                                        type="outline"
                                        titleColor={theme?.primary}
                                        outlineColor={theme?.primary}
                                        buttonStyle={style?.outlineButton}
                                        containerStyle={[style?.headerButtonContainer, { flex: 1 }]}
                                        disabled={isLoading}
                                        onPress={() => setPreview(true)}
                                    />
                                )}
                                {isSettingWrite && (
                                    <ZenButton
                                        title="Save"
                                        titleColor={theme?.light}
                                        buttonStyle={canSave ? style?.primaryButton : style?.disabledButton}
                                        containerStyle={[style?.headerButtonContainer, { flex: isLandscape ? 0 : 1 }]}
                                        disabled={!canSave}
                                        loading={saving}
                                        onPress={() => formik.handleSubmit()}
                                    />
                                )}
                            </View>
                        } />

                        <ScrollView
                            showsVerticalScrollIndicator={false}
                            keyboardShouldPersistTaps="handled"
                            contentContainerStyle={style?.scrollContent}
                        >
                            <View style={[style?.sectionsWrapper, { flexDirection: isLandscape ? "row" : "column", alignItems: isLandscape ? "flex-start" : undefined }]}>
                                <View style={[style?.column, { flex: isLandscape ? 1.3 : undefined }]}>
                                    {receiptTypeCard}
                                    {isLoading ? skeleton : <>
                                        {contentCard}
                                        {displayOptionsCard}
                                    </>}
                                </View>
                                {isLandscape && (
                                    <View style={[style?.column, { flex: 1 }]}>
                                        {isLoading ? <View style={[style?.card, style?.skeletonCard]}>
                                            <ZenSkeleton width={"45%"} height={18} />
                                            <ZenSkeleton height={400} borderRadius={8} />
                                        </View> : livePreviewCard}
                                    </View>
                                )}
                            </View>
                        </ScrollView>

                        {/* Receipt preview (portrait) */}
                        <CustomModal visible={preview} onRequestClose={() => setPreview(false)}>
                            <View style={[style?.previewModal, { paddingTop: insets?.top }]}>
                                <CommonHeader headerName="Receipt Preview" backIcon backIconOnpress={() => setPreview(false)} />
                                <ScrollView showsVerticalScrollIndicator={false}>
                                    <View style={[style?.previewModalContent, { width: isLandscape ? "50%" : "95%" }]}>
                                        <ReceiptPreview formikValues={formik?.values} receiptType={formik?.values?.receiptType} theme={theme} />
                                    </View>
                                </ScrollView>
                            </View>
                        </CustomModal>

                        {/* Unsaved changes confirmation */}
                        <Popover
                            isVisible={leaveVisible}
                            popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }}
                            onRequestClose={() => setLeaveVisible(false)}
                        >
                            <CommonDialogBox
                                type="EDIT"
                                replaceValue="page"
                                confirmation={(item: { id: number }) => {
                                    setLeaveVisible(false)
                                    if (item?.id === 2) {
                                        dispatch(pageDirty(false))
                                        navigation.goBack()
                                    }
                                }}
                            />
                        </Popover>

                        {/* Switching receipt type discards unsaved changes */}
                        <Popover
                            isVisible={Boolean(pendingType)}
                            popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }}
                            onRequestClose={() => setPendingType(null)}
                        >
                            <CommonDialogBox
                                type="OTHER"
                                dialog={{
                                    content: `You have unsaved changes. Switch to the ${pendingType === "GIFT" ? "gift" : "sales"} receipt and discard them?`,
                                    button: [{ id: 1, name: "Switch" }, { id: 2, name: "Cancel" }]
                                }}
                                confirmation={(item: { id: number }) => {
                                    const type = pendingType
                                    setPendingType(null)
                                    if (item?.id === 1 && type) {
                                        applyReceiptType(type)
                                    }
                                }}
                            />
                        </Popover>
                    </KeyboardAvoidingView>
                )
            }}
        </ThemeConsumer>
    )
}

export default ReceiptsSettings
